import { AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

/** A row's status tone: the dot colour and the tint of its status word. */
export type PickerTone = 'ok' | 'warn' | 'crit' | 'muted';

/**
 * Network picker panel — a command-palette-style list of networks: a filter, a status-dotted
 * list with HA / FIPS tags and a tone-tinted status word, a check on the current one, and an
 * optional footer. Presentational and stateless: pass `networks` + `currentNetworkId` in, act
 * on `(networkChange)`. The parent owns open/close and positions the panel (this only draws it).
 *
 * Shared by the dashboard header switcher and the User Settings default-network field so both
 * read the same way; the differences (header/footer/clear row) are inputs.
 */
@Component({
    selector: 'app-network-picker',
    standalone: true,
    imports: [CommonModule, FormsModule, RouterModule],
    templateUrl: './network-picker.component.html',
    styleUrls: ['./network-picker.component.scss'],
})
export class NetworkPickerComponent implements AfterViewInit {
    @Input() networks: any[] = [];
    @Input() currentNetworkId?: string;

    /** Header row (title + count). Off for the inline settings field. */
    @Input() showHeader = true;
    @Input() headerLabel = 'Switch network';
    /** Footer row (Manage Networks link + keyboard hints). */
    @Input() showFooter = false;
    @Input() manageLink = '/networks';
    @Input() manageLabel = 'Manage Networks';
    /** A leading "clear" row (settings uses it to unset the default network). */
    @Input() showClear = false;
    @Input() clearLabel = 'No default network';
    @Input() placeholder = 'Type to filter…';
    /** The plural noun for the items, used in the filter's label and the empty row. */
    @Input() itemNoun = 'networks';
    /** Read a row's tone and status word. The defaults read network statuses; other items pass their own. */
    @Input() toneOf: (item: any) => PickerTone = networkTone;
    @Input() statusWordOf: (item: any) => string = networkStatusWord;

    /** Emits the chosen network, or null when the clear row is picked. */
    @Output() networkChange = new EventEmitter<any>();
    /** Emits the filter text as it is typed, for a parent that searches on the server. */
    @Output() filterChange = new EventEmitter<string>();
    /** Asks the parent to close (Escape, a selection, or the Manage link). */
    @Output() closed = new EventEmitter<void>();

    @ViewChild('netSearch') private netSearch?: ElementRef<HTMLInputElement>;
    @ViewChild('pickerList') private pickerList?: ElementRef<HTMLElement>;

    netFilter = '';
    /** Keyboard-highlighted row (index into filteredNetworks). */
    activeIndex = 0;

    ngAfterViewInit(): void {
        setTimeout(() => this.netSearch?.nativeElement?.focus(), 0);
    }

    /** True when no network is the current one — the clear row reads as selected. */
    get clearIsCurrent(): boolean {
        return !this.currentNetworkId || this.currentNetworkId === 'default';
    }

    /** Networks matching the filter, current one first then alphabetical. */
    get filteredNetworks(): any[] {
        const term = this.netFilter.trim().toLowerCase();
        return (this.networks || [])
            .filter((n) => !term || (n?.name || '').toLowerCase().includes(term))
            .sort((a, b) => {
                if (a.id === this.currentNetworkId) return -1;
                if (b.id === this.currentNetworkId) return 1;
                return (a?.name || '').localeCompare(b?.name || '');
            });
    }

    /** Arrow-key navigation over the filtered list; Enter selects, Escape closes. */
    onSearchKeydown(ev: KeyboardEvent): void {
        const list = this.filteredNetworks;
        if (ev.key === 'ArrowDown') {
            ev.preventDefault();
            this.activeIndex = list.length ? (this.activeIndex + 1) % list.length : 0;
            this.scrollActiveIntoView();
        } else if (ev.key === 'ArrowUp') {
            ev.preventDefault();
            this.activeIndex = list.length ? (this.activeIndex - 1 + list.length) % list.length : 0;
            this.scrollActiveIntoView();
        } else if (ev.key === 'Enter') {
            ev.preventDefault();
            const net = list[this.activeIndex];
            if (net) {
                this.selectNetwork(net);
            }
        } else if (ev.key === 'Escape') {
            this.closed.emit();
        }
    }

    private scrollActiveIntoView(): void {
        setTimeout(() => {
            const items = this.pickerList?.nativeElement?.querySelectorAll('.np-item');
            (items?.[this.activeIndex] as HTMLElement | undefined)?.scrollIntoView({ block: 'nearest' });
        }, 0);
    }

    selectNetwork(network: any): void {
        this.networkChange.emit(network);
        this.closed.emit();
    }

    selectClear(): void {
        this.networkChange.emit(null);
        this.closed.emit();
    }

    onManage(): void {
        this.closed.emit();
    }

    statusDotClass(item: any): PickerTone {
        return this.toneOf(item);
    }

    statusWord(item: any): string {
        return this.statusWordOf(item);
    }
}

/** Dot tone from a network status: green active, red error, grey terminal, amber transitional. */
export function networkTone(network: any): PickerTone {
    const s = (network?.status || '').toUpperCase();
    if (s === 'PROVISIONED') {
        return 'ok';
    }
    if (s === 'ERROR') {
        return 'crit';
    }
    if (s === 'DELETING' || s === 'DELETED' || s === 'SUSPENDED') {
        return 'muted';
    }
    return 'warn';
}

/** A short status word, shown only for non-active networks so healthy rows stay quiet. */
export function networkStatusWord(network: any): string {
    const s = (network?.status || '').toUpperCase();
    if (!s || s === 'PROVISIONED') {
        return '';
    }
    if (s === 'ERROR') {
        return 'Error';
    }
    if (s === 'PROVISIONING' || s === 'UPDATING' || s === 'REPLACING' || s === 'BUILDING' || s === 'NEW') {
        return 'Provisioning';
    }
    return s.charAt(0) + s.slice(1).toLowerCase();
}
